import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  ArrowBack,
  Security,
  CheckCircle,
} from '@mui/icons-material';
import { FcGoogle } from 'react-icons/fc'; // Google icon
import api from '@/utils/api';
import { userSignIn } from '../api/user.api';

function SigninPage() {
  const navigate = useNavigate();
  const [isRequest, setIsRequest] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetRequest, setIsResetRequest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: yup.object({
      username: yup.string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters')
        .max(30, 'Username must be less than 30 characters'),
      password: yup.string()
        .required('Password is required')
        .min(6, 'Password must be at least 6 characters'),
    }),
    onSubmit: (values) => onSignIn(values),
  });

  const onSignIn = async ({ username, password }) => {
    if (isRequest) return;
    setIsRequest(true);

    const { response, err } = await userSignIn({ username, password });

    setIsRequest(false);

    if (response) {
      localStorage.setItem('tkn', response.token);
      localStorage.setItem('username', response.username);

      // Store tenant information directly from login response
      if (response.tenant) {
        localStorage.setItem('tenant_id', response.tenant.toString());
        // Dispatch a custom event so other components can react to tenant change
        window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { tenantId: response.tenant.toString() } }));
      }

      // Fetch fresh user data after successful login
      try {
        const userResponse = await fetch('/api/v1/users/current_user', {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${response.token}`,
          },
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const user = userData.user || userData;
          if (user && user.role) {
            localStorage.setItem('user', JSON.stringify(user));
            window.dispatchEvent(new CustomEvent('userUpdated'));
          }
        }
      } catch (userError) {
        console.warn('Failed to fetch user data after login:', userError);
      }

      const { fetchTenantHomePath } = await import('../utils/resolveTenantHome');
      const homePath = await fetchTenantHomePath({ token: response.token });
      navigate(homePath);
    }

    if (err) toast.error(err.message);
  };

  const handleGoogleLogin = () => {
    window.location.href = '/api/v1/auth/google';
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResetRequest(true);

    try {
      // Call your password reset API endpoint
      const response = await fetch('/api/v1/users/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Password reset link sent to your email!');
        setResetDialogOpen(false);
        setResetEmail('');
      } else {
        toast.error(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Error sending reset email. Please try again.');
    }

    setIsResetRequest(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        py: 2,
        px: 2,
        overflow: 'auto',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        },
      }}
    >
      {/* Back to Landing Page Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          color: 'white',
          zIndex: 10,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        Back to Home
      </Button>

      <Container maxWidth="sm" sx={{ my: 2 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#1976d2',
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              Sign in to your BaigLabs account
            </Typography>
          </Box>

          {/* Sign In Form */}
          <Box component="form" noValidate onSubmit={form.handleSubmit}>
            <Stack spacing={3}>
              {/* Username Field */}
              <TextField
                fullWidth
                placeholder="Enter your username"
                name="username"
                value={form.values.username}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.username && !!form.errors.username}
                helperText={form.touched.username && form.errors.username}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                name="password"
                value={form.values.password}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.password && !!form.errors.password}
                helperText={form.touched.password && form.errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setResetDialogOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: '#1976d2',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    },
                  }}
                >
                  Forgot Password?
                </Button>
              </Box>

              {/* Sign In Button */}
              <Button
                type="submit"
                size="large"
                variant="contained"
                disabled={isRequest}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#90caf9',
                  },
                  transition: 'all 0.3s ease',
                }}
                startIcon={isRequest ? <CircularProgress size={20} color="inherit" /> : <Security />}
              >
                {isRequest ? 'Signing in...' : 'Sign In'}
              </Button>

              {/* Divider */}
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  or continue with
                </Typography>
              </Divider>

              {/* Google Sign In */}
              <Button
                variant="outlined"
                size="large"
                startIcon={<FcGoogle />}
                onClick={handleGoogleLogin}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: '#e5e7eb',
                  color: '#374151',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#d1d5db',
                    backgroundColor: '#f9fafb',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Sign in with Google
              </Button>

              {/* Sign Up Link */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?
                  {' '}
                  <Button
                    component={Link}
                    to="/signup"
                    sx={{
                      textTransform: 'none',
                      color: '#1976d2',
                      fontWeight: 600,
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Sign up here
                  </Button>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>

      {/* Password Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" fontWeight={700}>
            Reset Password
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email address and we'll send you a secure link to reset your password.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            placeholder="Enter your email"
            variant="outlined"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setResetDialogOpen(false);
              setResetEmail('');
            }}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            disabled={!resetEmail || isResetRequest}
            startIcon={isResetRequest ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              textTransform: 'none',
            }}
          >
            {isResetRequest ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SigninPage;
